/**
 * "Craftable now" (sprint-assembly-1, AD-647) — the pure half of the crafting panel's
 * one-click list. The panel used to demand a typed recipe id and hand-picked input rows,
 * which a first-time player cannot know; this picks, from the recipes the client pack
 * already ships (`recipes`: inputs, station, masteryRank) and the player's own resource
 * stacks, every recipe the bag can pay for right now, with the exact stacks to send.
 *
 * It decides NOTHING the server does not re-check: the craftIntent it builds is the one
 * the manual form would send, and station presence, mastery rank, fees and quality are
 * all refused (or accepted) by the server as before. A recipe this lists in a zone with
 * no matching bench fails with the server's own reason, shown in "Last result".
 *
 * Stack choice: lowest acceptable quality first, so a player keeps their best material
 * for later — the choice a player would make by hand. Pure; node-testable.
 */

/** One resource input of a recipe (the client-pack projection of `RecipeDef.inputs`). */
export interface RecipeInputView {
  resource?: string;
  quantity: number;
  minQuality?: number;
}

/** The client-pack projection of one recipe the panel reads. */
export interface CraftRecipeView {
  id: string;
  name?: string;
  station?: string;
  masteryRank?: number;
  inputs?: readonly RecipeInputView[];
  output?: string;
  retired?: boolean;
}

/** One held resource stack (the `inventorySnapshot.resources` wire shape). */
export interface HeldStack {
  contentId: string;
  quality: number;
  quantity: number;
}

/** One stack to consume — exactly the `craftIntent` input wire shape. */
export interface CraftInputChoice {
  resource: string;
  quality: number;
  quantity: number;
}

/** A recipe the bag can pay for, with the stacks that pay for it. */
export interface CraftableRecipe {
  recipe: CraftRecipeView;
  inputs: CraftInputChoice[];
}

/**
 * Choose the stacks that pay for `recipe` from `held`, or `null` when the bag cannot.
 * Each input draws from stacks of its resource at or above its `minQuality`, lowest
 * quality first; two inputs of the same resource draw from what the first left.
 */
export function payFor(
  recipe: CraftRecipeView,
  held: readonly HeldStack[],
): CraftInputChoice[] | null {
  const inputs = recipe.inputs ?? [];
  if (inputs.length === 0) return null;
  const left = new Map<string, number>(); // `${resource}:${quality}` → still available
  for (const s of held)
    left.set(
      `${s.contentId}:${s.quality}`, // id-ok: map key
      (left.get(`${s.contentId}:${s.quality}`) ?? 0) + s.quantity, // id-ok: map key
    );
  const out: CraftInputChoice[] = [];
  for (const input of inputs) {
    if (input.resource === undefined) return null; // an item input: not a bag-of-stacks craft
    let need = input.quantity;
    const qualities = held
      .filter((s) => s.contentId === input.resource && s.quality >= (input.minQuality ?? 1))
      .map((s) => s.quality)
      .filter((q, i, all) => all.indexOf(q) === i)
      .sort((a, b) => a - b);
    for (const quality of qualities) {
      if (need === 0) break;
      const key = `${input.resource}:${quality}`; // id-ok: map key
      const take = Math.min(need, left.get(key) ?? 0);
      if (take <= 0) continue;
      left.set(key, (left.get(key) ?? 0) - take);
      out.push({ resource: input.resource, quality, quantity: take });
      need -= take;
    }
    if (need > 0) return null;
  }
  return out;
}

/**
 * Every non-retired recipe the bag can pay for, cheapest mastery first (a new player's
 * starter recipes lead the list), then by name. `limit` caps the list the panel draws.
 */
export function craftableRecipes(
  recipes: readonly CraftRecipeView[],
  held: readonly HeldStack[],
  limit = 8,
): CraftableRecipe[] {
  const out: CraftableRecipe[] = [];
  for (const recipe of recipes) {
    if (recipe.retired === true) continue;
    const inputs = payFor(recipe, held);
    if (inputs !== null) out.push({ recipe, inputs });
  }
  out.sort(
    (a, b) =>
      (a.recipe.masteryRank ?? 99) - (b.recipe.masteryRank ?? 99) ||
      (a.recipe.name ?? a.recipe.id).localeCompare(b.recipe.name ?? b.recipe.id),
  );
  return out.slice(0, limit);
}

/**
 * The inputs a WORKSHOP job sends for a batch of `quantity` (q-p2triage-fe-player-trust-
 * and-ux; the panel used to send `inputs: []`, which the server always refuses
 * `badInputs`). The server validates the inputs as ONE recipe's worth and then consumes
 * each chosen stack × `quantity` (workshop.ts §8.5) — so each stack is chosen from what the
 * bag holds divided by the batch size. `null` when the bag cannot pay for the whole batch.
 */
export function payForBatch(
  recipe: CraftRecipeView,
  held: readonly HeldStack[],
  quantity: number,
): CraftInputChoice[] | null {
  if (!Number.isInteger(quantity) || quantity < 1) return null;
  const perUnit = held.map((s) => ({ ...s, quantity: Math.floor(s.quantity / quantity) }));
  return payFor(recipe, perUnit);
}
